import { Graph } from 'graphs-for-js';
import { Node as ReactFlowNode } from 'reactflow';
import type {
  LineageGraphViewInput,
  LineageNodePositionInput,
} from '../../../services/gql/types';
import type {
  EnrichedLineageNode,
  LineageGraphResponse,
  LineageReactFlowEdge,
  ReactFlowGraph,
  ReactFlowNodeData,
  WorkerData,
} from '../types';
import { getLineageEdgeStyle, getNodesOnPath } from './lineageGraphUtils';

export type ParsedLineageGraph = {
  reactFlowGraph: ReactFlowGraph;
  workerData: WorkerData;
};

export const getLineageGraphView = (
  view: Partial<LineageGraphViewInput> = {},
): LineageGraphViewInput => ({
  expandedNodeIds: view.expandedNodeIds ?? [],
  collapsedNodeIds: view.collapsedNodeIds ?? [],
  highlightedNodeId: view.highlightedNodeId ?? null,
  lockedPositions: view.lockedPositions ?? [],
  loadedColumnsAssetIds: view.loadedColumnsAssetIds ?? [],
  forceLoadLargeGraph: view.forceLoadLargeGraph ?? false,
});

export const getLockedPositions = (
  nodeIds: string[] | undefined,
  nodes: ReactFlowNode<ReactFlowNodeData>[],
): LineageNodePositionInput[] =>
  (nodeIds ?? []).map((nodeId) => {
    const position = nodes.find((node) => node.id === nodeId)?.position;
    return { id: nodeId, x: position?.x ?? null, y: position?.y ?? null };
  });

const buildMetadataById = (metadata: LineageGraphResponse['metadata']) => {
  const entries = Object.entries(metadata).map(
    ([nodeId, { childIds, ...metadataFields }]) => {
      const node: EnrichedLineageNode = metadataFields;
      return { nodeId, node, childIds };
    },
  );
  const metadataById = new Map<string, EnrichedLineageNode>(
    entries.map(({ nodeId, node }) => [nodeId, node]),
  );
  entries.forEach(({ node, childIds }) => {
    if (!childIds) return;
    node.children = childIds
      .map((childId) => metadataById.get(childId))
      .filter((child): child is EnrichedLineageNode => !!child);
  });
  return metadataById;
};

const buildWorkerData = (
  metadataById: Map<string, EnrichedLineageNode>,
  graphEdges: LineageGraphResponse['graphEdges'],
): WorkerData => {
  const filteredGraph = new Graph<EnrichedLineageNode, string>()
    .keyFn((node) => node.id)
    .directed.unweighted();
  metadataById.forEach((node) => filteredGraph.insert(node));
  graphEdges.forEach(([sourceId, targetId]) => {
    const source = metadataById.get(sourceId);
    const target = metadataById.get(targetId);
    if (source && target) filteredGraph.connect(source, target);
  });
  return {
    graph: filteredGraph,
    filteredGraph,
    enrichedLineageData: {
      nodes: [...metadataById.values()],
      edges: graphEdges.map(([sourceId, targetId]): [string, string] => [
        targetId,
        sourceId,
      ]),
    },
  };
};

const buildReactFlowNodes = (
  responseNodes: LineageGraphResponse['nodes'],
  metadataById: Map<string, EnrichedLineageNode>,
) => {
  const nodes = responseNodes.flatMap(
    (node): ReactFlowNode<ReactFlowNodeData>[] => {
      const metadata = metadataById.get(node.id);
      if (!metadata) return [];
      return [
        {
          id: node.id,
          type: node.type,
          parentNode: node.parentNode,
          position: node.position,
          zIndex: node.zIndex,
          draggable: !node.parentNode,
          deletable: false,
          data: { ...node.data, metadata },
        },
      ];
    },
  );

  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  nodes.forEach((node) => {
    const parent = node.parentNode ? nodesById.get(node.parentNode) : undefined;
    if (!parent) return;
    parent.data.children = [...(parent.data.children ?? []), node];
  });
  nodes.forEach((node) => {
    const parent = node.parentNode ? nodesById.get(node.parentNode) : undefined;
    if (!parent) return;
    node.data.parentNode = {
      ...parent.data.metadata,
      children: parent.data.children?.map((child) => child.data.metadata),
    };
  });
  return nodes;
};

// The worker keeps nested children as separate copies flagged by the highlight
// path only; edge shifting reads those flags, so they must not alias the flat nodes.
const copyNestedChildrenWithHighlightPath = (
  nodes: ReactFlowNode<ReactFlowNodeData>[],
  workerData: WorkerData,
  highlightedNodeId?: string,
) => {
  const highlightedNode = workerData.filteredGraph
    ?.nodes()
    .find((node) => node.id === highlightedNodeId);
  if (!highlightedNode) return;
  const nodeIdsOnPath = new Set(
    getNodesOnPath(workerData, [highlightedNode], workerData.filteredGraph).map(
      (node) => node.id,
    ),
  );
  nodes.forEach((node) => {
    if (!node.data.children) return;
    node.data.children = node.data.children.map((child) => ({
      ...child,
      data: { ...child.data, isOnHighlightPath: nodeIdsOnPath.has(child.id) },
    }));
  });
};

export const parseLineageGraphResponse = (
  response: LineageGraphResponse,
  highlightedNodeId?: string,
): ParsedLineageGraph => {
  const metadataById = buildMetadataById(response.metadata);
  const workerData = buildWorkerData(metadataById, response.graphEdges);
  const nodes = buildReactFlowNodes(response.nodes, metadataById);
  copyNestedChildrenWithHighlightPath(nodes, workerData, highlightedNodeId);

  const edges: LineageReactFlowEdge[] = response.edges.map((edge) => ({
    id: edge.id,
    source: edge.source ?? '',
    target: edge.target ?? '',
    hidden: edge.hidden,
    zIndex: edge.zIndex,
    style: getLineageEdgeStyle(edge.variant, edge.highlight),
    data: { logicalEdges: edge.logicalEdges },
  }));

  return { reactFlowGraph: { nodes, edges }, workerData };
};
